<script>
import VueTagsInput from '@johmun/vue-tags-input'
import store from '@state/store'
import { debounce } from 'debounce'
import isArray from 'lodash/isArray'

const initialFiltersState = {
  name: '',
  location: '',
  cause_ids: [],
}

const initialTagsState = {
  items: [],
  input: '',
  selected: [],
}

export default {
  name: 'OrganizationsFilters',
  components: { VueTagsInput },
  data() {
    return {
      filters: { ...initialFiltersState },
      causes: { ...initialTagsState },
      initialized: false,
    }
  },
  computed: {
    causesInput() {
      return this.causes.input
    },
  },
  watch: {
    causesInput: 'getOrganizationCauses',
    filters: {
      async handler() {
        if (!this.initialized) return

        const filledFilters = Object.fromEntries(
          Object.entries(this.filters).filter(([key, value]) => {
            if (isArray(value) && !value.length) return false
            return !!value
          })
        )

        await store.dispatch('organizations/fetchFilteredOrganizations', {
          filters: filledFilters,
        })
      },
      deep: true,
    },
  },
  created() {
    this.getOrganizationCauses()
    // Initialize with empty filters to load all organizations
    store
      .dispatch('organizations/fetchFilteredOrganizations', {
        filters: {},
      })
      .then(() => {
        this.initialized = true
      })
  },
  methods: {
    clearFilters() {
      this.filters = { ...initialFiltersState }
      this.causes = { ...initialTagsState }
    },
    clearCauseFilters() {
      this.filters = {
        ...this.filters,
        cause_ids: [],
      }
      this.causes = { ...initialTagsState }
    },
    setFilterField: debounce(async function(e, field, minLength = 0) {
      const value = (e.target && e.target.value) || e
      this.filters[field] = value.length >= minLength ? value : ''
    }, 500),

    getOrganizationCauses() {
      clearTimeout(this.debounce)
      this.debounce = setTimeout(() => {
        store
          .dispatch('companies/fetchCauses')
          .then((response) => {
            this.causes.items = response.data.map((a) => {
              return { text: a.name, id: a.id }
            })
          })
          .catch(() => console.warn('Oh. Something went wrong'))
      }, 500)
    },

    updateOrganizationCauses(newCauses) {
      this.causes.selected = newCauses
      this.filters.cause_ids = this.causes.selected.map(({ id }) => id)
    },
  },
}
</script>

<template>
  <div class="flex space-x-4 my-6 pl-10 pr-22">
    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <div
        :class="{ 'text-blue': filters.name }"
        class="inline-flex items-center filter-dropdown"
      >
        <button
          v-if="filters.name"
          class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
          @click.prevent="setFilterField('', 'name')"
        >
          <BaseIcon name="close" />
        </button>
        <span class="mr-2 truncate">
          {{ filters.name || 'Name' }}
        </span>
      </div>

      <template slot="popover">
        <input
          class="text-center border-b border-black cursor-pointer w-58"
          placeholder="Search by name"
          :value="filters.name"
          @input="setFilterField($event, 'name', 2)"
        />
      </template>
    </v-popover>

    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <div
        :class="{ 'text-blue': filters.location }"
        class="inline-flex items-center filter-dropdown"
      >
        <button
          v-if="filters.location"
          class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
          @click.prevent="setFilterField('', 'location')"
        >
          <BaseIcon name="close" />
        </button>
        <span class="mr-2 truncate">
          {{ filters.location || 'Location' }}
        </span>
      </div>

      <template slot="popover">
        <input
          class="text-center border-b border-black cursor-pointer w-58"
          placeholder="City, state, zip"
          :value="filters.location"
          @input="setFilterField($event, 'location', 2)"
        />
      </template>
    </v-popover>

    <v-popover trigger="click" placement="bottom" :auto-hide="true">
      <div
        :class="{ 'text-blue': causes.selected.length }"
        class="inline-flex items-center filter-dropdown"
      >
        <button
          v-if="causes.selected.length"
          class="mr-1 -ml-2 rounded-full hover:bg-blue-lighter"
          @click.prevent="clearCauseFilters"
        >
          <BaseIcon name="close" />
        </button>
        <span class="mr-2 truncate">
          {{ (causes.selected.length && causes.selected[0].text) || 'Cause' }}
        </span>
      </div>

      <template slot="popover">
        <div class="w-58">
          <VueTagsInput
            v-model="causes.input"
            :tags="causes.selected"
            :autocomplete-items="causes.items"
            :add-only-from-autocomplete="true"
            :add-on-blur="false"
            placeholder="Filter by cause"
            :autocomplete-min-length="0"
            @tags-changed="updateOrganizationCauses"
          />
        </div>
      </template>
    </v-popover>

    <button
      class="inline-flex items-center text-base text-left button secondary-button"
      @click="clearFilters"
    >
      <BaseIcon name="close" class="mr-1" />
      <span>Clear Filters</span>
    </button>
  </div>
</template>

<style lang="scss" scoped>
/* stylelint-disable */
.vue-tags-input::v-deep {
  .ti-input {
    @apply border-0 border-b border-black rounded-none shadow-none;
  }
  .ti-new-tag-input {
    @apply text-center text-black;
  }
}
/* stylelint-enable */
</style>
